import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function listHtml(items) {
  if (!items?.length) return '<p>—</p>';
  return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
}

export function buildArabicPatientCopyHtml(copy, patientName = '') {
  const meds = copy?.medications || [];
  const medsHtml = meds.length
    ? meds
        .map(
          (med) => `
      <div class="med">
        <p><strong>اسم الدواء:</strong> ${escapeHtml(med.name)}</p>
        <p><strong>الجرعة:</strong> ${escapeHtml(med.dose)}</p>
        <p><strong>كيف تاخذه:</strong> ${escapeHtml(med.how_to_take)}</p>
        <p><strong>متى تاخذه:</strong> ${escapeHtml(med.when_to_take)}</p>
      </div>`
        )
        .join('')
    : '<p>—</p>';

  return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>ملخص خروج المريض</title>
  <style>
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", Tahoma, "Noto Naskh Arabic", "Arial", sans-serif;
    direction: rtl;
    text-align: right;
    color: #111;
    margin: 0;
    padding: 32px;
    line-height: 1.7;
    width: 794px;
    background: #fff;
  }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 16px; margin: 22px 0 8px; color: #1e40af; }
  .meta { color: #555; margin-bottom: 18px; }
  .med {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 10px 12px;
    margin-bottom: 10px;
  }
  ul { padding-right: 18px; margin: 0; }
  p { margin: 0 0 8px; }
  </style>
</head>
<body>
  <h1>ملخص خروج المريض</h1>
  <p class="meta">${escapeHtml(patientName || 'المريض')}</p>

  <h2>1. التشخيص</h2>
  <p>${escapeHtml(copy?.diagnosis) || '—'}</p>

  <h2>2. الأدوية</h2>
  ${medsHtml}

  <h2>3. التعليمات في البيت</h2>
  ${listHtml(copy?.home_instructions)}

  <h2>4. متى ترجع للطوارئ</h2>
  ${listHtml(copy?.return_to_er)}

  <h2>5. موعد المتابعة</h2>
  <p>${escapeHtml(copy?.follow_up) || '—'}</p>
</body>
</html>`;
}

function renderOffscreenHtml(html) {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '794px';
  iframe.style.height = '1123px';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    throw new Error('Could not render Arabic patient copy.');
  }

  doc.open();
  doc.write(html);
  doc.close();

  return { iframe, body: doc.body };
}

/** Generate and download the Arabic patient copy as a PDF file. */
export async function downloadArabicPatientCopyPdf(copy, patientName = '') {
  const html = buildArabicPatientCopyHtml(copy, patientName);
  const { iframe, body } = renderOffscreenHtml(html);

  try {
    await new Promise((resolve) => setTimeout(resolve, 150));
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 794,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const safeName = String(patientName || 'patient').replace(/\s+/g, '_');
    pdf.save(`Sawtna_PatientCopy_Arabic_${safeName}_${Date.now()}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
}
